import React, { useEffect, useState, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';

// Core Components (loaded immediately)
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import About from './components/About';
import Facilities from './components/Facilities';
import Programs from './components/Programs';
import Reviews from './components/Reviews';
import Contact from './components/Contact';
import FAQ from './components/FAQ';
import Footer from './components/Footer';
import SEOHead from './components/SEOHead';
import Achievements from './components/Achievements';
import StarPerformanceAd from './components/StarPerformanceAd';

// Pages - Loaded Immediately
import BookDemo from './pages/BookDemo';
import Coaches from './pages/Coaches';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Refund from './pages/Refund';
import FloatingContactWidget from './components/FloatingContactWidget';

// Pages - Lazy Loaded (Code Splitting)
const AdminLogin = React.lazy(() => import('./pages/AdminLogin'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));

// Loading fallback component
const LoadingFallback = () => (
  <div className="min-h-screen bg-black flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-red-600"></div>
  </div>
);

// --- UTILS: SCROLL MANAGEMENT ---
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
    } else {
      const id = hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        // Delay slightly to ensure component is rendered
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [pathname, hash]);

  return null;
}

// --- SECURITY: ADMIN PROTECTED ROUTE ---
const ProtectedRoute = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );
  
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
};

// --- LAYOUT: MAIN LANDING PAGE ---
function MainSite() {
  return (
    <main>
      <SEOHead 
        title="Chaitanya Shooting Academy | Train Like A Champion"
        description="Elite precision shooting academy in Gurugram. Learn from NRAI certified coaches. Olympic-standard facilities. Book your free trial today!"
      />
      <Hero />
      <About />
      <Facilities />
      <Achievements />
      <Programs />
      <FAQ />
      <Reviews />
      <Contact />
      {/* High-Converting Star Performance Visitor Announcement Ad / Spotlight Modal */}
      <StarPerformanceAd />
    </main>
  );
}

// --- ROOT APP COMPONENT ---
function App() {
  return (
    <Router>
      <div className="bg-[#050505] min-h-screen font-sans selection:bg-[#B91C1C] selection:text-white">
        <ScrollToTop />
        
        {/* We use a conditional check to hide Navbar/Footer on Dashboard if preferred, 
            but usually, keeping Navbar is fine. */}
        <Navbar />
        
        <Routes>
          {/* Public Branding Routes */}
          <Route path="/" element={<MainSite />} />
          <Route path="/book-demo" element={<BookDemo />} />
          <Route path="/coaches" element={<Coaches />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/refund" element={<Refund />} />
          
          {/* Admin Access & Secure Dashboard - Lazy Loaded */}
          <Route 
            path="/admin/login" 
            element={
              <Suspense fallback={<LoadingFallback />}>
                <AdminLogin />
              </Suspense>
            } 
          />
          <Route 
            path="/admin/dashboard" 
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <AdminDashboard />
                </Suspense>
              </ProtectedRoute>
            } 
          />

          {/* Fallback Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {/* --- PROFESSIONAL FOOTER --- */}
        <ConditionalFooter />
        
        {/* --- FLOATING WHATSAPP & PHONE CONTACT WIDGET --- */}
        <FloatingContactWidget />
      </div>
    </Router>
  );
}

// Conditional Footer - Hide on admin pages
function ConditionalFooter() {
  const location = useLocation();
  const hideFooter = location.pathname.includes('admin');
  
  if (hideFooter) return null;
  return <Footer />;
}

export default App;